import { useEffect, useMemo, useState } from "react";
import Header from "../../Header";
import LoadingSpinner from "../../Shared/LoadingSpinner";
import VolumesAPI from "../../../API/VolumesAPI";
import PlanDetailsAPI from "../../../API/PlanDetailsAPI";
import HybridPlanAPI from "../HybridVolume/HybridPlanAPI";
import SprayProgramAPI from "./SprayProgramAPI";
import { bundleCheck, resistancePrices } from "../HybridVolume/bundleCheck";
import { calcVolumes, defaultThresholds } from "../TotalVolume/calcVolumes";
import { RESISTANCE_GROUP_LIST } from "./resistanceGroupList";
import { ANDER_TARGET, blankRow, categoriesOf, computeProgram, dependantRows, offeredProducts, packOptions, pickCategory, pickChoice, pickTarget, productsOf, targetsOf } from "./programCalc";
import { createProgramPlan, plantingBody, programItems } from "./programPlan";
import "./SprayProgram.css";

// The new spuit program (potatoes only), in the layout of the customer's prototype:
// record header, three cards (Farm, MyPack tier, SILVALIS weerstand volumes), the spray table, the resistance groups,
// and the order worked out from the program. "Skep bestelling" creates a normal plan and order, then opens BESTELLING.

const n0 = (v) => (v === null || v === undefined ? "" : Math.round(v).toLocaleString("en-ZA").replace(/,/g, " "));
const n2 = (v) => (v === null || v === undefined ? "" : v.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const n3 = (v) => (v === null || v === undefined ? "" : v.toLocaleString("en-ZA", { maximumFractionDigits: 3 }));
const R = (v) => (v === null || v === undefined ? "" : `R ${n2(v)}`);
const TIER = { 0: "No tier", 1: "Tier 1", 2: "Tier 2", 3: "Tier 3" };
const CHECK_CLASS = { Reg: "good", "Bo maks": "warn", "Onder min": "warn", "Geen limiete": "neutral" };
const NO_LIMIT = 999;
// Where "Skep bestelling" goes after the order is created. TEMPORARY: "priceView" (the price screen, to compare the prices);
// change to "orderView" to go to BESTELLING instead.
const AFTER_CREATE = "priceView";

// Row tooltips for the order table: how the two cost figures are worked out, with this row's own numbers
const costTips = (l) => {
  const vol = n3(l.volume);
  const list = `${vol} L ordered × R ${n2(l.listPrice)} list price = R ${n2(l.listValue)}\n\nSource: pricing2.list_price_pv`;
  const before = l.volume > 0 ? (l.listValue - l.discount) / l.volume : l.listPrice;
  const why = [];
  if (l.isResistance && l.resistancePriced) why.push(`Silvalis bundle met: the resistance price R ${n2(l.unitPrice)} replaces the tier price on the whole volume (pricing2.resistance_price_pv).`);
  else if (l.discount > 0) why.push(`${l.appliedTier ? TIER[l.appliedTier] : "Tier"} discount: R ${n2(l.discount)} off the list value. Average price after discount: R ${n2(before)} per L.`);
  else why.push("No tier discount applies to this product, so the price stays at the list price.");
  if (l.seasonCapped) why.push(`The season price R ${n2(l.row.season_price_pv)} (pricing2.season_price_pv) is lower than R ${n2(before)}, so the season price is used (*).`);
  const mypack = `${vol} L × R ${n2(l.unitPrice)} MyPack price = R ${n2(l.myPackValue)}\n\n${why.join("\n")}`;
  let price;
  if (l.seasonCapped) price = `* Season price. The price after discount was R ${n2(before)} per L, but the season price for this product is R ${n2(l.row.season_price_pv)}, which is lower, so it is used.\n\nSource: pricing2.season_price_pv`;
  else if (l.isResistance && l.resistancePriced) price = `Silvalis resistance price (bundle met).\n\nSource: pricing2.resistance_price_pv`;
  else if (l.discount > 0) price = `${l.appliedTier ? TIER[l.appliedTier] : "Tier"} price: list R ${n2(l.listPrice)} less the discount, per L.\n\nSource: pricing2 (tier1_price_pv, tier2_price_pv, tier3_discount_pct)`;
  else price = `No discount: the list price.\n\nSource: pricing2.list_price_pv`;
  return { list, mypack, price };
};

const TIPS = {
  category: "Die kategorie van die produk (insekdoders, swamdoders ...). Dit begin die keuse: kategorie, dan teiken, dan produk.\n\nBron: tabel categories (via choices).",
  target: "Die plaag of siekte waarvoor gespuit word. Net die teikens van die gekose kategorie word gewys.\n\nBron: tabel choices (productType).",
  product: "Die produkte wat vir hierdie kategorie en teiken gekies kan word, met hul aanbevole dosis. 'Ander' is vir 'n produk wat nie op die lys is nie; tik sy naam. Dit word nie geprys nie.\n\nBron: tabel choices en products.",
  pack: "Die verpakking wat bestel word. Daar word altyd heel bokse bestel vir die hele plaas.\n\nBron: tabel pricing2 (product_pack_name).",
  group: "Die produk se weerstandsgroep (IRAC / FRAC / HRAC). Het die produk nie een nie, kies jy dit hier.\n\nBron: products.resistanceGroup.",
  dosis: "Dosis per hektaar per spuit. Dit begin by die produk se eerste aanbevole dosis; jy kan dit verander.\n\nBron: choices.dosage.",
  week: "Merk die weke waarin gespuit word. Die kolomme is die gewas se periodes.\n\nBron: tabel periods.",
  sprays: "Aantal weke gemerk.\n\nBron: jou merke in die weke-kolomme.",
  volHa: "Spuite × dosis. Is 'n produk op meer as een reël, dan tel die reëls saam by die toets.\n\nBron: die Sprays- en Dosis-kolomme.",
  min: "Kleinste dosis per ha waarvoor die Vlak 2-afslag geld.\n\nBron: pricing2.tier2_min_dosage.",
  max: "Grootste dosis per ha. Net die volume tot (maks × hektaar) kwalifiseer vir die vlak-afslag.\n\nBron: pricing2.max_dosage.",
  check: "Reg = die produk se totale dosis per ha is binne sy minimum en maksimum. Bo maks / Onder min = daarbuite. Geen limiete = geen minimum of maksimum.\n\nBron: pricing2.tier2_min_dosage en pricing2.max_dosage.",
  listHa: "Vol/ha × lysprys.\n\nBron: Vol/ha en pricing2.list_price_pv.",
  mypackHa: "Vol/ha × MyPack prys van die verpakking (sien die bestelling hieronder).\n\nBron: die MyPack prys-kolom hieronder.",
  needed: "Volume van die spuitreëls × hektaar.\n\nBron: die programreëls en die Hectares-veld.",
  boxes: "Volume nodig ÷ pakgrootte, na bo afgerond, vir die hele plaas.\n\nBron: pricing2.full_pack_volume.",
  ordered: "Bokse × pakgrootte.\n\nBron: bokse × pricing2.full_pack_volume.",
  listPrice: "Lysprys per liter of kg.\n\nBron: pricing2.list_price_pv (of die agent se eie lysprys).",
  mypackPrice: "Gemiddelde prys na afslag. * = die laer seisoenprys is toegepas. Is die weerstandsvoorwaardes nagekom, geld die weerstandsprys op die hele volume.\n\nBron: pricing2 (tier1_price_pv, tier2_price_pv, tier3_discount_pct, season_price_pv, resistance_price_pv).",
  listCost: "Volume × lysprys.\n\nBron: volume × pricing2.list_price_pv.",
  mypackCost: "Volume × MyPack prys.\n\nBron: bereken uit die kolomme links.",
  weeks: "Die weke waarin 'n produk van hierdie groep gespuit word.\n\nBron: jou merke in die spuitprogram.",
};
const tip = (text) => ({ "data-tip": text });

const groupOptions = (current) => {
  const sets = { FRAC: [], IRAC: [], HRAC: [], Other: [] };
  RESISTANCE_GROUP_LIST.forEach((g) => (sets[/^(FRAC|IRAC|HRAC)/.exec(g)?.[1] || "Other"] || sets.Other).push(g));
  return (
    <>
      <option value="">Pick group…</option>
      {Object.entries(sets)
        .filter(([, list]) => list.length)
        .map(([label, list]) => (
          <optgroup key={label} label={label}>
            {list.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </optgroup>
        ))}
    </>
  );
};

const SprayProgram = (props) => {
  const cropId = props.cropId || 1;
  const [pricing, setPricing] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [allChoices, setAllChoices] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [farm, setFarm] = useState("");
  const [orderName, setOrderName] = useState("");
  const [hectares, setHectares] = useState("100");
  const [bundlePick, setBundlePick] = useState({}); // "<main>:<group>" -> productId (Sorba or Denim Fit)
  const [thresholds, setThresholds] = useState({ tier1MinRpha: 0, tier2MinRpha: 0, tier3MinInvoice: 0 });
  const [rows, setRows] = useState([]);
  const [notice, setNotice] = useState("");
  const [tipBox, setTipBox] = useState(null);
  const [plan, setPlan] = useState({ open: false, busy: false, error: "", farmers: [], farmerId: "", name: "", crops: [], done: null });
  const patchPlan = (patch) => setPlan((p) => ({ ...p, ...patch }));
  const soon = (text) => {
    setNotice(text);
    setTimeout(() => setNotice(""), 6000);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      VolumesAPI.getVolumePricing(cropId),
      VolumesAPI.getResistanceBundles(cropId).catch(() => null),
      SprayProgramAPI.getChoices(cropId),
      HybridPlanAPI.getPeriods(),
    ])
      .then(([pricingData, bundleData, choiceData, periodData]) => {
        if (cancelled) return;
        const list = (periodData || []).filter((p) => p.crop_id === cropId).sort((a, b) => a.order_seq - b.order_seq);
        setPricing(pricingData);
        setThresholds(defaultThresholds(pricingData));
        setBundles(Array.isArray(bundleData) ? bundleData : []);
        setAllChoices(choiceData);
        setPeriods(list);
        setRows([blankRow(list.length)]);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [cropId]);

  const ha = Number(hectares) || 0;
  const products = useMemo(() => offeredProducts(allChoices), [allChoices]);
  const computed = useMemo(() => computeProgram({ rows, products, pricing, hectares: ha }), [rows, products, pricing, ha]);
  // null = each bundle group's min_cover from the database (one rule for every order)
  const checks = useMemo(() => bundleCheck(pricing, computed.lines, bundles, null, bundlePick), [pricing, computed.lines, bundles, bundlePick]);
  const resistance = useMemo(() => resistancePrices(pricing, checks), [pricing, checks]);
  const result = useMemo(() => calcVolumes(pricing, computed.lines, ha, thresholds, { resistance }), [pricing, computed.lines, ha, thresholds, resistance]);
  const built = useMemo(() => programItems(computed), [computed]);
  const dbShortfallPct = useMemo(() => {
    const covers = (bundles || []).flatMap((b) => (b.groups || []).map((g) => Number(g.minCover))).filter((v) => v > 0);
    return covers.length ? Math.round((1 - Math.min(...covers)) * 1000) / 10 : 2;
  }, [bundles]);
  const missingPartners = useMemo(
    () => checks.filter((c) => c.hasMain).flatMap((c) => dependantRows({ check: c, products, pricing, rows, periodCount: periods.length }).added),
    [checks, products, pricing, rows, periods.length]
  );

  const patchRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const categories = useMemo(() => categoriesOf(allChoices), [allChoices]);
  const pickRowCategory = (key, value) => setRows((rs) => rs.map((r) => (r.key === key ? pickCategory(r, value ? Number(value) : null) : r)));
  const pickRowTarget = (key, value) => setRows((rs) => rs.map((r) => (r.key === key ? pickTarget(r, value ? Number(value) : null, allChoices) : r)));
  const pickRowChoice = (key, value) => setRows((rs) => rs.map((r) => (r.key === key ? pickChoice(allChoices, pricing, r, value ? Number(value) : null) : r)));
  const toggleWeek = (key, index) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, weeks: r.weeks.map((w, i) => (i === index ? !w : w)) } : r)));
  const addRow = () => setRows((rs) => [...rs, blankRow(periods.length)]);
  const removeRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : [blankRow(periods.length)]));

  const addDependants = () => {
    let added = [];
    const skipped = [];
    checks
      .filter((c) => c.hasMain)
      .forEach((c) => {
        const d = dependantRows({ check: c, products, pricing, rows, periodCount: periods.length });
        added = added.concat(d.added);
        skipped.push(...d.skipped);
      });
    setRows((rs) => [...rs.filter((r) => r.productId), ...added]);
    const names = added.map((r) => (products.find((p) => p.productId === r.productId) || {}).name);
    const parts = [added.length ? `${names.join(", ")} bygevoeg met dieselfde weke.` : "Daar is niks om by te voeg nie."];
    if (skipped.length) parts.push(`${skipped.join(", ")} is reeds in die program of het geen keuse nie.`);
    soon(parts.join(" "));
  };

  const openPlan = async () => {
    if (!built.items.length) {
      soon("Maak eers 'n spuitreël klaar: kies 'n produk, 'n dosis en merk die weke.");
      return;
    }
    patchPlan({ open: true, busy: true, error: "", done: null, name: orderName || farm || "" });
    try {
      const [farmers, crops] = await Promise.all([HybridPlanAPI.getFarmers(), HybridPlanAPI.getCrops()]);
      patchPlan({ farmers, crops, busy: false });
    } catch (e) {
      patchPlan({ busy: false, error: e.message });
    }
  };

  const submitPlan = async () => {
    patchPlan({ busy: true, error: "" });
    try {
      const cropName = (plan.crops.find((c) => c.id === cropId) || {}).name;
      const name = plan.name.trim();
      const out = await createProgramPlan({
        api: HybridPlanAPI,
        cropId,
        planting: { farmerId: plan.farmerId, body: plantingBody(name, ha, cropName) },
        planName: name,
        built,
        periods,
      });
      let planObj = null;
      try {
        const all = await PlanDetailsAPI.getAllPlans(false, props.agentDetail && props.agentDetail.id, cropId);
        planObj = (all || []).find((p) => p.id === out.plan.id) || null;
      } catch (e) {
        /* fall back below */
      }
      const next = { ...(planObj || out.plan), fromProgram: true };
      if (props.setPlanDetails) {
        props.setPlanDetails(next);
        // the price screen needs the back path; without setBackPath (not passed from App.js) go to BESTELLING
        const target = AFTER_CREATE === "priceView" && props.setBackPath ? "priceView" : "orderView";
        if (target === "priceView") props.setBackPath("orderView");
        props.setPath(target);
      } else {
        patchPlan({ busy: false, done: { plan: out.plan } });
      }
    } catch (e) {
      patchPlan({
        busy: false,
        error: e.planId ? `${e.message}. Program "${e.planName}" (nr ${e.planId}) is gedeeltelik geskep: verwyder dit in die program-lys en probeer weer.` : e.message,
      });
    }
  };

  // custom tooltip, as in the prototype: any element with data-tip
  const onPointerMove = (e) => {
    const t = e.target && e.target.closest ? e.target.closest("[data-tip]") : null;
    if (!t) {
      if (tipBox) setTipBox(null);
      return;
    }
    setTipBox({ text: t.getAttribute("data-tip"), x: Math.min(e.clientX + 14, window.innerWidth - 340), y: e.clientY + 14 });
  };

  if (loading) return <LoadingSpinner text="Laai pryse..." />;

  const { totals, currentTier, nextTier } = result;
  const lineByPack = new Map(result.lines.map((l) => [l.rowId, l]));
  const weekName = (i) => (periods[i] ? periods[i].name : "");
  const mainCheck = checks[0] || null;
  const hasOrder = result.lines.some((l) => l.boxes > 0);
  const tierPillClass = currentTier > 0 ? "good" : "warn";
  const T = ({ children, k, className = "" }) => (
    <th className={className} {...tip(TIPS[k])}>
      {children}
    </th>
  );

  return (
    <div className="sp-root" translate="no" onPointerMove={onPointerMove} onPointerLeave={() => setTipBox(null)}>
      <Header title="NUWE SPUITPROGRAM" backPath="pastOrders" setPath={props.setPath} />
      <div className="main">
        <header className="record">
          <div>
            <div className="crumbs">Spuit program / Nuwe</div>
            <h1>{farm.trim() || "Nuwe spuitprogram"}</h1>
            <div className="record-meta">
              <span className="pill neutral">Spuit program</span>
              <span>{ha ? `${n3(ha)} ha` : "No hectares yet"}</span>
              <span className={`pill ${tierPillClass}`}>{TIER[currentTier]}</span>
              {mainCheck && <span className={`pill ${mainCheck.pass ? "good" : "bad"}`}>SILVALIS afslag {mainCheck.pass ? "Ja" : "Nee"}</span>}
              <span className="num">{R(totals.totalList)}</span>
            </div>
          </div>
          <div className="actions">
            <button type="button" className="primary" disabled={plan.open} onClick={openPlan}>
              Skep bestelling
            </button>
          </div>
        </header>
        {error && <div className="error">{error}</div>}
        <div className="toast" role="status" aria-live="polite">
          {notice}
        </div>

        <div className="cols">
          <div className="card">
            <h2>Farm</h2>
            <div className="field">
              <label htmlFor="sp-farm">Plaasnaam</label>
              <input id="sp-farm" type="text" autoComplete="off" value={farm} onChange={(e) => setFarm(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="sp-order">Order name</label>
              <input id="sp-order" type="text" autoComplete="off" placeholder="Naam van die program" value={orderName} onChange={(e) => setOrderName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="sp-ha">Hectares</label>
              <input id="sp-ha" type="number" min="0" step="any" inputMode="decimal" value={hectares} onChange={(e) => setHectares(e.target.value)} />
            </div>
          </div>

          <div className="card">
            <h2>MyPack tier</h2>
            <dl className="kv">
              <dt>List R/ha</dt>
              <dd>{R(totals.listRpha)}</dd>
              <dt>Total list invoice</dt>
              <dd>{R(totals.totalList)}</dd>
              <dt>Current tier</dt>
              <dd>
                <span className={`pill ${tierPillClass}`}>{TIER[currentTier]}</span>
              </dd>
            </dl>
            <div className="verdict warn">
              {nextTier ? `R ${n0(nextTier.moreNeeded)} more for ${TIER[nextTier.tier]}` : "Top tier reached"}
              <small>Next tier</small>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Needs</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Tier 1</td>
                  <td className="num">R {n0(thresholds.tier1MinRpha)} per ha</td>
                </tr>
                <tr>
                  <td>Tier 2</td>
                  <td className="num">R {n0(thresholds.tier2MinRpha)} per ha</td>
                </tr>
                <tr>
                  <td>Tier 3</td>
                  <td className="num">R {n0(thresholds.tier3MinInvoice)} invoice</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <h2>SILVALIS weerstand volumes</h2>
            {checks.length === 0 && <p className="note">Geen weerstandsprodukte vir hierdie gewas nie.</p>}
            {checks.map((c) => {
              const okCount = c.partners.filter((p) => p.ok).length;
              const missing = c.partners.filter((p) => !p.ok).map((p) => p.name);
              const verdictClass = c.pass ? "good" : "warn";
              return (
                <div key={c.mainProductId} style={{ display: "grid", gap: 14 }}>
                  <div className={`verdict ${verdictClass}`}>
                    {!c.hasMain ? `Geen ${c.mainName}` : c.pass ? `${c.mainName} discount given` : `${missing.join(", ")} te min`}
                    <small>
                      {c.mainName} in program: {n0(c.mainLitres)} L · {okCount} of {c.partners.length} companions at their minimum
                    </small>
                  </div>
                  <table className="req">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Volume</th>
                        <th>Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.partners.map((p) => (
                        <tr key={p.key}>
                          <td>
                            {p.options.length > 1 ? (
                              <select value={p.productId} onChange={(e) => setBundlePick((ch) => ({ ...ch, [p.key]: Number(e.target.value) }))} aria-label="Partner">
                                {p.options.map((o) => (
                                  <option key={o.productId} value={o.productId}>
                                    {o.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              p.name
                            )}
                          </td>
                          <td className="num" {...tip(`${n0(c.hectaresCovered)} ha × ${n3(p.dose)} × ${p.sprays} spuite = ${n3(p.need)}; minus ${n3(p.shortfall * 100)}% tekort, na bo afgerond = ${n0(p.needed)}\n\nBron: pricing2Dependants (dose_per_spray, sprays_per_season, min_cover).`)}>
                            {n0(p.needed)}
                          </td>
                          <td className={`num ${c.hasMain ? (p.ok ? "ok" : "short") : ""}`}>{n0(p.ordered)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="actions">
                    <button type="button" disabled={!c.hasMain || missingPartners.length === 0} onClick={addDependants} title={!c.hasMain ? `Voeg eers ${c.mainName} by` : missingPartners.length ? "Voeg die vennote by met dieselfde weke" : "Al die vennote is reeds in die program"}>
                      Voeg afhanklike produkte by
                    </button>
                  </div>
                  <p className="note">
                    {c.mainName} afslag: <span className={`pill ${c.pass ? "good" : "bad"}`}>{c.pass ? "Ja" : "Nee"}</span>. All partners ({c.partners.map((p) => p.options.map((o) => o.name).join(" or ")).join(", ")}) at their minimum qualify, with a {n3(dbShortfallPct)}% shortfall allowance (from the database). This check only sets the {c.mainName} price; it never blocks the order.
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <h2>Spuit program</h2>
          <p className="note">
            Build the program from scratch: pick a category and target (from SST Choices), the product and its pack, the dose per spray, and tick when it is sprayed. Volume per ha = number of sprays × dose. The order is worked out in whole boxes for the farm.
          </p>
          <div className="scroll">
            <table className="order program">
              <thead>
                <tr>
                  <T k="category">Category</T>
                  <T k="target">Target</T>
                  <T k="product">Product</T>
                  <T k="pack">Pack</T>
                  <T k="group">Group</T>
                  <T k="dosis">
                    Dosis
                    <br />
                    L/kg per ha
                  </T>
                  {periods.map((p, i) => (
                    <T key={p.id || p.order_seq} k="week" className="wk">
                      {p.name}
                    </T>
                  ))}
                  <T k="sprays">Sprays</T>
                  <T k="volHa">Vol/ha</T>
                  <T k="min">Min vol/ha</T>
                  <T k="max">Max vol/ha</T>
                  <T k="check">Check</T>
                  <T k="listHa">List R/ha</T>
                  <T k="mypackHa">MyPack R/ha</T>
                  <th>
                    <span hidden>Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {computed.rows.map((r, i) => {
                  const row = r.row;
                  const n = i + 1;
                  const packs = r.product && !r.other ? packOptions(pricing, r.product.productId) : [];
                  const line = r.pack ? lineByPack.get(r.pack.id) : null;
                  const min = r.pack && Number(r.pack.tier2_min_dosage) > 0 ? Number(r.pack.tier2_min_dosage) : null;
                  const max = r.pack && Number(r.pack.max_dosage) > 0 && Number(r.pack.max_dosage) < NO_LIMIT ? Number(r.pack.max_dosage) : null;
                  const doseHint = r.product && r.product.doses.length ? `Aanbevole dosisse: ${r.product.doses.map(n3).join(" / ")} per ha per spuit\n(die dosis van die gekose keuse is gebruik; jy kan dit verander)\n\nBron: tabel choices (dosage)` : null;
                  return (
                    <tr key={row.key}>
                      <td>
                        <select value={row.categoryId || ""} onChange={(e) => pickRowCategory(row.key, e.target.value)} aria-label={`Category, spray ${n}`}>
                          <option value="">Pick a category…</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select value={row.targetId || ""} disabled={!row.categoryId} onChange={(e) => pickRowTarget(row.key, e.target.value)} aria-label={`Target, spray ${n}`}>
                          <option value="">Pick a target…</option>
                          {targetsOf(allChoices, row.categoryId).map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {row.targetId !== ANDER_TARGET && (
                          <select value={row.choiceId || ""} disabled={!row.targetId} onChange={(e) => pickRowChoice(row.key, e.target.value)} aria-label={`Product, spray ${n}`}>
                            <option value="">Pick a product…</option>
                            {productsOf(allChoices, row.categoryId, row.targetId).map((p) => (
                              <option key={p.choiceId} value={p.choiceId}>
                                {p.other ? "Ander (other product)" : p.name + (p.dose > 0 ? ` · ${n3(p.dose)}/ha` : "")}
                              </option>
                            ))}
                          </select>
                        )}
                        {r.other && <input className="other" type="text" placeholder="Product name" aria-label={`Other product name, spray ${n}`} value={row.otherName} onChange={(e) => patchRow(row.key, { otherName: e.target.value })} />}
                      </td>
                      <td>
                        {!r.product || r.other ? (
                          <span className="muted">–</span>
                        ) : packs.length ? (
                          <select value={row.packRowId || ""} onChange={(e) => patchRow(row.key, { packRowId: Number(e.target.value) })} aria-label={`Pack, spray ${n}`}>
                            {packs.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.product_pack_name || p.product_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="pill warn">Not in price list</span>
                        )}
                      </td>
                      <td className="left">
                        {r.product && !r.product.group ? (
                          <select className={`group${row.group ? "" : " missing"}`} value={row.group} onChange={(e) => patchRow(row.key, { group: e.target.value })} aria-label={`Resistance group, spray ${n}`}>
                            {groupOptions(row.group)}
                          </select>
                        ) : r.group ? (
                          <span className={`pill ${/verify|unknown/i.test(r.group) ? "warn" : "neutral"}`}>{r.group}</span>
                        ) : null}
                      </td>
                      <td className="dose" {...(doseHint ? tip(doseHint) : {})}>
                        <input type="number" min="0" step="any" inputMode="decimal" aria-label={`Dose per spray, spray ${n}`} value={row.dose} onChange={(e) => patchRow(row.key, { dose: e.target.value === "" ? "" : Number(e.target.value) })} />
                      </td>
                      {periods.map((p, k) => (
                        <td key={p.id || p.order_seq} className="wk">
                          <input type="checkbox" checked={!!row.weeks[k]} onChange={() => toggleWeek(row.key, k)} aria-label={`${p.name}, spray ${n}`} />
                        </td>
                      ))}
                      <td className="num">{r.sprays ? n0(r.sprays) : ""}</td>
                      <td className="num">{r.volHa ? n3(r.volHa) : ""}</td>
                      <td className="num">{r.priced && min ? n3(min) : ""}</td>
                      <td className="num">{r.priced && max ? n3(max) : ""}</td>
                      <td>{r.check ? <span className={`pill ${CHECK_CLASS[r.check] || "neutral"}`}>{r.check}</span> : r.complete && !r.priced && r.volHa ? <span className="pill neutral">Not priced</span> : null}</td>
                      <td className="num">{r.priced && r.volHa ? n2(r.volHa * Number(r.pack.list_price_pv)) : ""}</td>
                      <td className="num">{r.priced && r.volHa && line ? n2(r.volHa * line.unitPrice) : ""}</td>
                      <td>
                        <button type="button" className="link danger" onClick={() => removeRow(row.key)} aria-label={`Remove spray ${n}`}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="actions">
            <button type="button" onClick={addRow}>
              Add spray
            </button>
          </div>

          <h2>Resistance groups</h2>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Group (IRAC / FRAC / HRAC)</th>
                  <th className="left">Products</th>
                  <th>Sprays</th>
                  <th className="left" {...tip(TIPS.weeks)}>
                    Weeks
                  </th>
                </tr>
              </thead>
              <tbody>
                {computed.groupsSummary.length ? (
                  computed.groupsSummary.map((g) => (
                    <tr key={g.group}>
                      <td>
                        <span className={`pill ${/verify|unknown/i.test(g.group) ? "warn" : "neutral"}`}>{g.group}</span>
                      </td>
                      <td className="left">{g.products.join(", ")}</td>
                      <td className="num">{n0(g.sprays)}</td>
                      <td className="left">{g.weeks.map(weekName).join(", ")}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="empty">
                      Tick weeks to see the resistance groups sprayed.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="note">
            Groups come from the Spuit Program sheet (products.resistanceGroup). A product without a group on file can be given one on its row, but it is optional: without one it is listed as Unknown and the order is created as normal. Amber = marked “verify” on the sheet, or not known.
          </p>

          <h2>Order from this program</h2>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Pack</th>
                  <th>Code</th>
                  <th {...tip(TIPS.needed)}>Volume needed (L/kg)</th>
                  <th {...tip(TIPS.boxes)}>Boxes</th>
                  <th {...tip(TIPS.ordered)}>Volume ordered</th>
                  <th {...tip(TIPS.listPrice)}>List price (R/L)</th>
                  <th {...tip(TIPS.mypackPrice)}>MyPack price (R/L)</th>
                  <th {...tip(TIPS.listCost)}>List cost (R)</th>
                  <th {...tip(TIPS.mypackCost)}>MyPack cost (R)</th>
                </tr>
              </thead>
              <tbody>
                {result.lines.map((l) => (
                  <tr key={l.key}>
                    <td>{l.row ? l.row.product_pack_name || l.row.product_name : ""}</td>
                    <td className="num">{l.row ? l.row.AGI : ""}</td>
                    <td className="num">{n3(l.litres)}</td>
                    <td className="num">{n0(l.boxes)}</td>
                    <td className="num">{n3(l.volume)}</td>
                    <td className="num">{n2(l.listPrice)}</td>
                    <td className="num" {...(l.row ? tip(costTips(l).price) : {})}>
                      {n2(l.unitPrice)}
                      {l.seasonCapped ? "*" : ""}
                    </td>
                    <td className="num" {...(l.row ? tip(costTips(l).list) : {})}>{n2(l.listValue)}</td>
                    <td className="num" {...(l.row ? tip(costTips(l).mypack) : {})}>{n2(l.myPackValue)}</td>
                  </tr>
                ))}
                {computed.unpriced.map((u) => (
                  <tr key={u.name} className="sp-unpriced">
                    <td>
                      {u.name} <span className="pill neutral">Not priced</span>
                    </td>
                    <td />
                    <td className="num">{n3(u.litres)}</td>
                    <td colSpan={6} className="muted left">
                      Other product – not in the MyPack price list or invoice
                    </td>
                  </tr>
                ))}
                {!hasOrder && computed.unpriced.length === 0 && (
                  <tr>
                    <td colSpan={9} className="empty">
                      {ha ? "Tick the weeks a product is sprayed to build the order." : "Enter the hectares to work out the order."}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td />
                  <td />
                  <td className="num">{n0(totals.totalBoxes)}</td>
                  <td />
                  <td />
                  <td />
                  <td className="num">{n2(totals.totalList)}</td>
                  <td className="num">{n2(totals.totalMyPack)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <dl className="kv" style={{ maxWidth: 420 }}>
            <dt>List cost per ha</dt>
            <dd>{R(totals.listRpha)}</dd>
            <dt>MyPack cost per ha</dt>
            <dd>{R(totals.myPackRpha)}</dd>
            <dt>MyPack saving vs list</dt>
            <dd>{R(totals.totalDiscount)}</dd>
          </dl>
          <div className="actions">
            <button type="button" className="primary" disabled={plan.open || !built.items.length} onClick={openPlan}>
              Skep bestelling
            </button>
          </div>
          <p className="note">Products outside the price list (Ander or not priced) are listed in the order with their volume, but without boxes or prices, and are left out of the MyPack invoice. Dose checks add up all rows of a product. The SILVALIS check uses the boxes that are ordered.</p>
        </div>

        {plan.open && (
          <div className="card">
            <h2>Skep bestelling</h2>
            {plan.done ? (
              <>
                <div className="verdict good">Program &quot;{plan.done.plan.planName}&quot; is geskep.</div>
                <div className="actions">
                  <button type="button" className="primary" onClick={() => props.setPath("pastOrders")}>
                    Gaan na bestelling
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="plan-fields">
                  <div className="field">
                    <label htmlFor="sp-client">Kies &#39;n kliënt</label>
                    <select id="sp-client" value={plan.farmerId} onChange={(e) => patchPlan({ farmerId: e.target.value })}>
                      <option value="">Kies kliënt</option>
                      {plan.farmers.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.businessName || f.farmName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="sp-name">Program naam</label>
                    <input id="sp-name" type="text" value={plan.name} onChange={(e) => patchPlan({ name: e.target.value })} />
                  </div>
                  <div className="field">
                    <label htmlFor="sp-plan-ha">Aanplanting grootte (Ha)</label>
                    <input id="sp-plan-ha" type="text" value={String(ha)} disabled readOnly />
                  </div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>Produk</th>
                      <th>Volume</th>
                      <th>Dosis per spuit</th>
                      <th>Spuite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {built.items.map((it) => (
                      <tr key={it.rowKey}>
                        <td>{it.name}</td>
                        <td className="num">{n3(it.volume)}</td>
                        <td className="num">{n3(it.dosage)}</td>
                        <td className="num">{it.ticks.filter(Boolean).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {built.skipped.map((x) => (
                  <div key={x.name + x.reason} className="verdict warn">
                    {x.name} word nie ingesluit nie: {x.reason}.
                  </div>
                ))}
                {plan.error && <div className="error">{plan.error}</div>}
                <div className="actions">
                  <button type="button" className="primary" disabled={plan.busy || !plan.farmerId || !plan.name.trim() || !built.items.length} onClick={submitPlan}>
                    Skep program en gaan na bestelling
                  </button>
                  <button type="button" disabled={plan.busy} onClick={() => patchPlan({ open: false })}>
                    Kanseleer
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <p className="note">Beraming. Die finale prys word op die bestelling bepaal. * = seisoenprys toegepas.</p>
      </div>
      {tipBox && (
        <div className="sp-tip" style={{ left: tipBox.x, top: tipBox.y }}>
          {tipBox.text}
        </div>
      )}
    </div>
  );
};

export default SprayProgram;
